import React, { useMemo } from "react";
import { UncontrolledTooltip } from "reactstrap";
import { Button, Icon, Col, Row, RSelect } from "../../../../components/Component";
import http from "../../../../utils/http-common";
import swal from "sweetalert";
import { mergeCountryFilterOptions, resolveIsoCountrySelectValue } from "../../../../utils/iso3166Alpha2Options";
import CountryCodeSelect from "../../../../components/common/CountryCodeSelect";
import PartnerDetailInlineValue from "../../../../components/features/medical-partner-admin/PartnerDetailInlineValue";
import MpOverviewStatus from "../../../../components/features/medical-partner-admin/MpOverviewStatus";
import MpFieldRow from "../../../../components/features/medical-partner-admin/MpFieldRow";
import MpClinicMetrics from "../../../../components/features/medical-partner-admin/MpClinicMetrics";
import { emptyOrText, dialPhoneParts, formatCountryForDisplay } from "./partnerOverviewFormatters";
import { slugifyOrganizationName } from "./helpers";
import { medicalPartnerPutConfig } from "../../../../utils/partnerEditLockRequest";
import "./medical-partner-details.css";

/**
 * Partner dossier — Overview tab: identity, public slug, TC/TM status, clinic counts.
 * Admins can edit TC/TM and workflow here; per-clinic TC and MT flags live on Teleconsultation / Clinics.
 */
const PartnerDetailsOverviewTab = ({
  partner,
  hasPrimaryUser,
  onOpenTeleconsultationTab,
  onOpenClinicsTab,
  inlineEdit = false,
  inlineFieldBusy = null,
  onPatchPartnerField,
  editingSlug,
  setEditingSlug,
  slugValue,
  setSlugValue,
  savingSlug,
  setSavingSlug,
  partnerSlugForUrl,
  partnerPublicPageUrl,
  primaryDirectoryAccountEmail = "",
  primaryDirectoryUserId = "",
  primaryUserLoginActive = null,
  enableUserLoginBusy = false,
  onEnableUserLogin,
  clinicMetrics,
  totalClinics,
  setPartner,
  refetchPartners,
  tcSaving = false,
  onSavePartnerTc,
  mtProgramSaving = false,
  onToggleMedicalTourismProgram,
  workflowBusy = false,
  onPartnerWorkflowStatusChange,
  partnerEditLockToken = null,
  dossierEditBlocked = false,
}) => {
  const primaryEmail =
    typeof primaryDirectoryAccountEmail === "string" ? primaryDirectoryAccountEmail.trim() : "";
  const slugRulesTargetId = partner?._id ? `partner-overview-slug-rules-${partner._id}` : "partner-overview-slug-rules";
  const dirEmailTipId = partner?._id ? `partner-overview-dir-email-${partner._id}` : "partner-overview-dir-email";
  const { display: phoneDisplay, telHref: phoneTel } = dialPhoneParts(
    partner?.contact_person_country_code,
    partner?.contact_person_phone
  );
  const canEditPrograms =
    Boolean(inlineEdit) &&
    typeof onSavePartnerTc === "function" &&
    typeof onToggleMedicalTourismProgram === "function";
  const canEditWorkflow = Boolean(inlineEdit) && typeof onPartnerWorkflowStatusChange === "function";

  const contactCountrySelectOptions = useMemo(
    () => mergeCountryFilterOptions([partner?.contact_person_country]),
    [partner?.contact_person_country]
  );

  const contactFieldBusy = Boolean(inlineFieldBusy);

  const saveSlug = async () => {
    if (!partner?._id) return;
    if (dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setSavingSlug(true);
    try {
      const slugPayload = { slug: slugValue.trim() || null };
      const res = await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        slugPayload,
        medicalPartnerPutConfig(partnerEditLockToken, slugPayload)
      );
      const next = res.data?.data || res.data;
      if (next) setPartner((prev) => ({ ...prev, ...next }));
      setEditingSlug(false);
      setSlugValue("");
      refetchPartners?.();
      swal("Success", "Slug updated", "success");
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update slug", "error");
    } finally {
      setSavingSlug(false);
    }
  };

  const allocateSlugFromServer = async () => {
    if (!partner?._id) return;
    if (dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setSavingSlug(true);
    try {
      const allocPayload = { allocatePartnerSlug: true };
      const res = await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        allocPayload,
        medicalPartnerPutConfig(partnerEditLockToken, allocPayload)
      );
      const next = res.data?.data || res.data;
      if (next) setPartner((prev) => ({ ...prev, ...next }));
      refetchPartners?.();
      swal("Success", "Public URL slug created", "success");
    } catch (err) {
      swal("Error", err.response?.data?.message || "Could not create slug", "error");
    } finally {
      setSavingSlug(false);
    }
  };

  const pathPreview = partnerSlugForUrl ? `/partners/${partnerSlugForUrl}` : null;

  const profileCompletion = (() => {
    const p = partner;
    if (!p) return null;
    const essential = [
      p.organization_name,
      p.contact_person_phone,
      p.contact_person_city,
      p.contact_person_country,
    ];
    const optional = [
      p.organization_logo,
      p.business_website,
      p.business_registration,
      hasPrimaryUser,
      partnerSlugForUrl,
    ];
    const filledE = essential.filter(Boolean).length;
    const filledO = optional.filter(Boolean).length;
    return Math.round((filledE / essential.length) * 60 + (filledO / optional.length) * 40);
  })();

  const profileMissing = (() => {
    if (!partner) return [];
    const m = [];
    if (!partner.organization_name) m.push("Organization name");
    if (!hasPrimaryUser) m.push("Primary account (Team tab)");
    if (!partnerSlugForUrl) m.push("Public slug");
    if (!partner.contact_person_city) m.push("City");
    if (!partner.contact_person_country) m.push("Country");
    if (!partner.contact_person_phone) m.push("Phone");
    return m.slice(0, 3);
  })();

  return (
    <div className="partner-details-overview">
      {profileCompletion !== null && profileCompletion < 100 ? (
        <div className="partner-overview-completion card card-bordered mb-4" role="region" aria-label={`Profile completion ${profileCompletion}%`}>
          <div className="card-inner py-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <span className="partner-overview-completion__label">Profile completion</span>
              <span className="partner-overview-completion__pct fw-bold" aria-hidden="true">{profileCompletion}%</span>
            </div>
            <div
              className="partner-overview-completion__bar"
              role="progressbar"
              aria-valuenow={profileCompletion}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuetext={`${profileCompletion}% complete`}
            >
              <div
                className="partner-overview-completion__bar-fill"
                style={{ width: `${profileCompletion}%` }}
              />
            </div>
            {profileMissing.length > 0 ? (
              <p className="text-soft small mb-0 mt-2">
                Missing: {profileMissing.join(" · ")}
                {profileMissing.length === 3 ? " ·\u00a0…" : ""}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <MpOverviewStatus
        partner={partner}
        hasUser={hasPrimaryUser}
        loginEmail={primaryEmail}
        primaryDirectoryUserId={primaryDirectoryUserId}
        primaryUserLoginActive={primaryUserLoginActive}
        enableUserLoginBusy={enableUserLoginBusy}
        onEnableUserLogin={onEnableUserLogin}
        totalClinics={totalClinics}
        partnerSlugForUrl={partnerSlugForUrl}
        tcSaving={tcSaving}
        mtSaving={mtProgramSaving}
        workflowBusy={workflowBusy}
        canEditPrograms={canEditPrograms}
        canEditWorkflow={canEditWorkflow}
        onTcChange={onSavePartnerTc}
        onMtChange={onToggleMedicalTourismProgram}
        onWorkflowChange={onPartnerWorkflowStatusChange}
      />

      <MpClinicMetrics
        metrics={clinicMetrics}
        total={totalClinics}
        hasUser={hasPrimaryUser}
        onClinics={onOpenClinicsTab}
        onTeleconsultation={onOpenTeleconsultationTab}
      />

      <Row className="g-gs">
        <Col md="6">
          <div className="partner-details-overview__panel card card-bordered h-100">
            <div className="card-inner">
              <h5 className="partner-details-overview__panel-title mb-0">Organization</h5>
              <dl className="partner-details-overview__dl">
                <MpFieldRow label="Organization name">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-organization_name"
                      value={partner.organization_name}
                      busy={inlineFieldBusy === "organization_name"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("organization_name", v)}
                    />
                  ) : (
                    emptyOrText(partner.organization_name)
                  )}
                </MpFieldRow>
                <MpFieldRow label="Website">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-business_website"
                      value={partner.business_website}
                      busy={inlineFieldBusy === "business_website"}
                      display="url"
                      onSave={(v) => onPatchPartnerField("business_website", v)}
                    />
                  ) : partner.business_website ? (
                    <a href={partner.business_website} target="_blank" rel="noopener noreferrer">
                      {partner.business_website}
                    </a>
                  ) : (
                    emptyOrText(null)
                  )}
                </MpFieldRow>
                <MpFieldRow label="Registration number">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-business_registration"
                      value={partner.business_registration}
                      busy={inlineFieldBusy === "business_registration"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("business_registration", v)}
                    />
                  ) : (
                    emptyOrText(partner.business_registration)
                  )}
                </MpFieldRow>
                <MpFieldRow label="User type">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-user_type"
                      value={partner.user_type}
                      busy={inlineFieldBusy === "user_type"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("user_type", v)}
                    />
                  ) : (
                    emptyOrText(partner.user_type)
                  )}
                </MpFieldRow>
              </dl>
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="partner-details-overview__panel card card-bordered h-100">
            <div className="card-inner">
              <h5 className="partner-details-overview__panel-title mb-0">Contact</h5>
              <dl className="partner-details-overview__dl">
                <MpFieldRow label="Contact name">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-contact_person_name"
                      value={partner.contact_person_name}
                      busy={inlineFieldBusy === "contact_person_name"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("contact_person_name", v)}
                    />
                  ) : (
                    emptyOrText(partner.contact_person_name)
                  )}
                </MpFieldRow>
                <MpFieldRow label="Directory login email">
                  {hasPrimaryUser && primaryEmail ? (
                    <span className="d-inline-flex flex-wrap align-items-center">
                      <span className="text-break">{primaryEmail}</span>
                      <span
                        id={dirEmailTipId}
                        className="d-inline-flex align-items-center"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: "help" }}
                      >
                        <Icon name="info-fill" className="text-soft" style={{ fontSize: "1rem" }} />
                      </span>
                      <UncontrolledTooltip placement="top" target={dirEmailTipId}>
                        Login and notifications use this directory user. Edit email and name in <strong>Full profile form</strong> → <strong>Primary user</strong>.
                      </UncontrolledTooltip>
                    </span>
                  ) : hasPrimaryUser ? (
                    <span className="partner-details-overview__empty">Not loaded</span>
                  ) : (
                    <span className="partner-details-overview__empty">Link a primary account on the Team tab</span>
                  )}
                </MpFieldRow>
                {inlineEdit ? (
                  <div className="partner-details-overview__row">
                    <div className="partner-details-overview__label">Calling code &amp; phone</div>
                    <div className="partner-details-overview__value">
                      <Row className="g-2 align-items-end">
                        <Col md="7" sm="12">
                          <span className="d-block small text-soft mb-1">Country calling code</span>
                          <CountryCodeSelect
                            id={`partner-overview-dial-${partner?._id || "partner"}`}
                            value={partner.contact_person_country_code}
                            onChange={(digits) => {
                              void onPatchPartnerField("contact_person_country_code", digits || "");
                            }}
                            disabled={contactFieldBusy}
                            placeholder="Country code"
                          />
                        </Col>
                        <Col md="5" sm="12">
                          <span className="d-block small text-soft mb-1">Phone (local)</span>
                          <PartnerDetailInlineValue
                            id="partner-inline-contact_person_phone"
                            value={partner.contact_person_phone}
                            busy={inlineFieldBusy === "contact_person_phone"}
                            display="text"
                            inputMode="tel"
                            autoComplete="tel-national"
                            onSave={(v) => onPatchPartnerField("contact_person_phone", v)}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                ) : (
                  <>
                    <MpFieldRow label="Country calling code">
                      {emptyOrText(partner.contact_person_country_code)}
                    </MpFieldRow>
                    <MpFieldRow label="Phone (local)">
                  {phoneDisplay ? (
                    phoneTel ? (
                      <a href={phoneTel}>
                        {phoneDisplay}
                      </a>
                    ) : (
                      phoneDisplay
                    )
                  ) : (
                        emptyOrText(null)
                      )}
                    </MpFieldRow>
                  </>
                )}
                <MpFieldRow label="Address">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-contact_person_address"
                      value={partner.contact_person_address}
                      busy={inlineFieldBusy === "contact_person_address"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("contact_person_address", v)}
                    />
                  ) : (
                    emptyOrText(partner.contact_person_address)
                  )}
                </MpFieldRow>
                <MpFieldRow label="City">
                  {inlineEdit ? (
                    <PartnerDetailInlineValue
                      id="partner-inline-contact_person_city"
                      value={partner.contact_person_city}
                      busy={inlineFieldBusy === "contact_person_city"}
                      display="text"
                      onSave={(v) => onPatchPartnerField("contact_person_city", v)}
                    />
                  ) : (
                    emptyOrText(partner.contact_person_city)
                  )}
                </MpFieldRow>
                <MpFieldRow label="Country (ISO)">
                  {inlineEdit ? (
                    <RSelect
                      inputId={`partner-overview-contact-country-${partner?._id || "partner"}`}
                      options={contactCountrySelectOptions}
                      value={resolveIsoCountrySelectValue(partner.contact_person_country) || null}
                      onChange={(opt) => {
                        void onPatchPartnerField(
                          "contact_person_country",
                          opt?.value != null ? String(opt.value).trim() : ""
                        );
                      }}
                      placeholder="Select country…"
                      isClearable
                      isDisabled={contactFieldBusy}
                    />
                  ) : formatCountryForDisplay(partner.contact_person_country) ? (
                    <span className="text-break">{formatCountryForDisplay(partner.contact_person_country)}</span>
                  ) : (
                    emptyOrText(null)
                  )}
                </MpFieldRow>
              </dl>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="g-gs mt-3">
        <Col lg="12">
          <div className="partner-details-overview__panel partner-details-overview__slug-panel card card-bordered">
            <div className="card-inner">
              <div className="d-flex flex-wrap align-items-start justify-content-between">
                <div className="flex-grow-1 pr-md-3 mb-3 mb-md-0" style={{ minWidth: "14rem" }}>
                  <h5 className="partner-details-overview__panel-title mb-2">Public profile &amp; URL</h5>
                  <p className="text-soft small mb-0">
                    The slug is the URL segment for this partner on the patient site (example path below). It can be edited separately without
                    opening the full organization dialog.
                  </p>
                </div>
                {!editingSlug ? (
                  <div className="d-flex flex-wrap flex-shrink-0 align-items-center">
                    {!partnerSlugForUrl ? (
                      <Button
                        color="primary"
                        size="sm"
                        type="button"
                        className="mr-2 mb-2"
                        disabled={savingSlug}
                        onClick={allocateSlugFromServer}
                      >
                        Generate slug
                      </Button>
                    ) : null}
                  <Button
                    color="light"
                    size="sm"
                    type="button"
                      className="mb-2"
                    onClick={() => {
                      setSlugValue(partnerSlugForUrl || "");
                      setEditingSlug(true);
                    }}
                  >
                    <Icon name="edit-alt-fill" className="mr-1" />
                    Edit slug
                  </Button>
                  </div>
                ) : null}
              </div>

              <div className="partner-details-overview__slug-body">
                {editingSlug ? (
                  <div className="d-flex flex-wrap align-items-center partner-details-overview__fx mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "18rem", minWidth: "12rem" }}
                      value={slugValue}
                      onChange={(e) => setSlugValue(e.target.value)}
                      placeholder="e.g. gaia-dental"
                      aria-label="Public URL slug"
                    />
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => {
                        const s = slugifyOrganizationName(partner.organization_name || "");
                        if (s) setSlugValue(s);
                      }}
                    >
                      Suggest from name
                    </Button>
                    <Button color="primary" size="sm" disabled={savingSlug} type="button" onClick={saveSlug}>
                      Save
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setEditingSlug(false);
                        setSlugValue("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex flex-wrap align-items-baseline partner-details-overview__fx">
                    <span className="text-dark fw-medium">{partnerSlugForUrl || "—"}</span>
                    {pathPreview ? <code className="small text-muted mb-0 bg-light px-2 py-1 rounded">{pathPreview}</code> : null}
                  </div>
                )}

                {partnerPublicPageUrl ? (
                  <div className="partner-details-overview__slug-preview">
                    <span className="partner-details-overview__slug-preview-url">{partnerPublicPageUrl}</span>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => {
                        window.open(partnerPublicPageUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(partnerPublicPageUrl).then(
                          () => swal("Copied", "Public URL copied to clipboard", "success"),
                          () => swal("Error", "Could not copy", "error")
                        );
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                ) : null}

                <div className="partner-details-overview__slug-help partner-details-overview__slug-help--compact mt-2">
                  {!partnerSlugForUrl && !editingSlug ? (
                    <p className="mb-2 mb-md-2 text-soft small">
                      <strong>No public link yet.</strong> Use <strong>Generate slug</strong> or <strong>Edit slug</strong> so patients can open this partner on the public site.
                    </p>
                  ) : null}
                  <p className="mb-0 text-soft small d-flex flex-wrap align-items-center">
                    <span className="mr-1">Lowercase, numbers, hyphens; unique per partner.</span>
                    <span id={slugRulesTargetId} className="d-inline-flex align-items-center" role="button" tabIndex={0} style={{ cursor: "help" }}>
                      <Icon name="info-fill" className="text-primary" style={{ fontSize: "1rem" }} />
                    </span>
                    <UncontrolledTooltip placement="top" target={slugRulesTargetId}>
                      Use only lowercase letters, numbers, and hyphens. Each slug must be unique. Example path: /partners/your-slug
                    </UncontrolledTooltip>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {partner.remarks ? (
        <Row className="g-gs mt-3">
          <Col md="12">
            <div className="partner-details-overview__panel card card-bordered">
              <div className="card-inner">
                <h5 className="partner-details-overview__panel-title mb-0">Remarks</h5>
                <p className="text-soft mb-0">{partner.remarks}</p>
              </div>
            </div>
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default PartnerDetailsOverviewTab;
